import { Col, Drawer, Row } from "antd";
import { useHistory, useLocation, useParams } from "react-router-dom";
import "../../Shared/SEMAnalysis.scss";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchSemAnalysisMaskDataRequest,
  fetchBlackObjectsBatchInfoRequest,
  downloadBatchReportRequest,
} from "src/store/actions/semAnalysis";
import { StoreState } from "src/store/configureStore";
import { AsyncStates } from "src/constants";
import { LeftOutlined, RightOutlined, VerticalAlignBottomOutlined } from "@ant-design/icons";
import { IMAGE_ANALYSIS_TYPES } from "../../SEMAnalysisWrapper";
import { StyledButton } from "src/styled_components/StyledButton";
import useTranslate from "src/utils/useTranslate";
// import BlackObjectAnalysisAttributes from "./BlackObjectAnalysisAttributes";
import BlackObjectAnalysisDetailsSidebar from "./BlackObjectAnalysisDetailsSidebar";
import BlackObjectAnalysisDetailsContent from "./BlackObjectAnalysisDetailsContent";



const BlackObjectAnalysisDetails = () => {
  const [t] = useTranslate();
  const location = useLocation<any>();
  const params = useParams<any>();
  const history = useHistory();
  const dispatch = useDispatch();
  const [area, setArea] = useState<number[]>();

  const [showAllMasks, setShowAllMasks] = useState(
    location.state?.showAllMasks ?? true
  );
  const [showBBox, setShowBBox] = useState(location.state?.showBBox ?? true);
  const [showOriginalImage, setShowOriginalImage] = useState(
    location.state?.showOriginalImage ?? true
  );
  const [showSkeleton, setShowSkeleton] = useState(true);
  const [hoveredMaskDetails, setHoveredMaskDetails] = useState<Record<string, any>>({});
  const {
    semAnalysisMaskData,
    blackObjectsBatchInfo,
    analysisTypes,
    modifyBlackObjectsStatus,
    downloadBatchReportStatus
  } = useSelector((state: StoreState) => state.semAnalysis);

  const fetchMasks = useCallback(() => {
    dispatch(
      fetchSemAnalysisMaskDataRequest({
        file_id: params.fileId,
        batch_id: params.batchId,
        analysis_type:
          analysisTypes[
          IMAGE_ANALYSIS_TYPES.BLACK_OBJECT_ANALYSIS as keyof typeof analysisTypes
          ],
      })
    );
  }, [analysisTypes, dispatch, params.batchId, params.fileId]);

  useLayoutEffect(() => {
    fetchMasks();
  }, [fetchMasks]);

  useEffect(() => {
    if (modifyBlackObjectsStatus === AsyncStates.SUCCESS) fetchMasks();
  }, [fetchMasks, modifyBlackObjectsStatus]);

  useEffect(() => {
    if (
      !blackObjectsBatchInfo ||
      (Array.isArray(blackObjectsBatchInfo) && blackObjectsBatchInfo.length === 0)
    ) {
      dispatch(
        fetchBlackObjectsBatchInfoRequest({
          batch_id: params?.batchId,
          analysis_type:
            analysisTypes[
            IMAGE_ANALYSIS_TYPES.BLACK_OBJECT_ANALYSIS as keyof typeof analysisTypes
            ],
        })
      );
    }
  }, [blackObjectsBatchInfo, dispatch, params?.batchId, analysisTypes]);

  const maskData = useMemo(
    () => {
      if (!area) return semAnalysisMaskData?.mask_results;
      else {
        return semAnalysisMaskData?.mask_results?.filter((mask: any) => mask.pixel_area >= area[0] && mask.pixel_area <= area[1])
      }
    }, [semAnalysisMaskData, area]
  );

  const imageToShow = useMemo(() => {
    return showOriginalImage
      ? semAnalysisMaskData?.original_image_url
      : semAnalysisMaskData?.binary_image_url;
  }, [semAnalysisMaskData, showOriginalImage]);

  const files = useMemo(() => {
    if (
      blackObjectsBatchInfo &&
      Array.isArray(blackObjectsBatchInfo) &&
      blackObjectsBatchInfo.length > 0
    ) {
      const currentBatch = blackObjectsBatchInfo.find((batch) => batch?.batch_id);

      return currentBatch?.thumbnails || [];
    }

    return [];
  }, [blackObjectsBatchInfo]);

  const onDrawerClose = () =>
    history.push(
      `/sem-analysis/${IMAGE_ANALYSIS_TYPES.BLACK_OBJECT_ANALYSIS}/batch/${params.batchId}`
    );

  const goNext = () => {
    const fileId = params.fileId;
    const currentFileIndex = files.findIndex(
      (file: any) => file.file_id === fileId
    );
    const nextFileId = files[currentFileIndex + 1]?.file_id;

    history.push({
      pathname: `/sem-analysis/${IMAGE_ANALYSIS_TYPES.BLACK_OBJECT_ANALYSIS}/batch/${params.batchId}/files/${nextFileId}`,
      state: { showAllMasks, showBBox, showOriginalImage },
    });
  };

  const goPrevious = () => {
    const fileId = params.fileId;
    const currentFileIndex = files.findIndex(
      (file: any) => file.file_id === fileId
    );
    const prevFileId = files[currentFileIndex - 1]?.file_id;

    history.push({
      pathname: `/sem-analysis/${IMAGE_ANALYSIS_TYPES.BLACK_OBJECT_ANALYSIS}/batch/${params.batchId}/files/${prevFileId}`,
      state: { showAllMasks, showBBox, showOriginalImage },
    });
  };

  const hasPreviousFile = () => {
    const fileId = params.fileId;
    const currentFileIndex = files.findIndex(
      (file: any) => file.file_id === fileId
    );
    return currentFileIndex <= 0 ? false : true;
  };

  const hasNextFile = () => {
    const fileId = params.fileId;
    const currentFileIndex = files.findIndex(
      (file: any) => file.file_id === fileId
    );
    return currentFileIndex >= files.length - 1 ? false : true;
  };

  const onDownloadReport = useCallback(() => {
    if (!params?.batchId || !params?.fileId) return;
    const payload: any = {
      batch_id: params.batchId,
      file_ids: params.fileId ? [params.fileId] : [],
      analysis_type:
        analysisTypes[
        IMAGE_ANALYSIS_TYPES.BLACK_OBJECT_ANALYSIS as keyof typeof analysisTypes
        ],
    };

    dispatch(downloadBatchReportRequest(payload));
  }, [analysisTypes, dispatch, params.batchId, params.fileId]);

  return (
    <Drawer
      title={"Black Object Analysis"}
      placement="bottom"
      open={true}
      height="100%"
      styles={{
        body: { padding: 0 },
      }}
      onClose={onDrawerClose}
      className="fiber-analysis-drawer"
      extra={
        <>
          {files && files.length > 0 && (
            <Row gutter={[8, 8]}>
              <Col>

                <StyledButton
                  type="primary"
                  onClick={() => onDownloadReport()}
                  style={{ marginRight: 10 }}
                  icon={<VerticalAlignBottomOutlined />}
                  disabled={modifyBlackObjectsStatus === AsyncStates.LOADING}
                  loading={downloadBatchReportStatus === AsyncStates.LOADING}
                >
                  {t("report.downloadReport")}
                </StyledButton>
              </Col>

              <Col>
                <StyledButton
                  type="link"
                  disabled={!hasPreviousFile()}
                  onClick={goPrevious}
                >
                  <LeftOutlined /> {t("sem.previousImage")}
                </StyledButton>
              </Col>
              <Col>
                <StyledButton
                  type="link"
                  disabled={!hasNextFile()}
                  onClick={goNext}
                >
                  {t("sem.nextImage")} <RightOutlined />
                </StyledButton>
              </Col>
            </Row>
          )}
        </>
      }
    >
      <div className="container">
        <BlackObjectAnalysisDetailsSidebar
          showOriginalImage={showOriginalImage}
          setShowOriginalImage={setShowOriginalImage}
          showAllMasks={showAllMasks}
          setShowAllMasks={setShowAllMasks}
          showBBox={showBBox}
          setShowBBox={setShowBBox}
          showSkeleton={showSkeleton}
          setShowSkeleton={setShowSkeleton}
          hoveredMaskDetails={hoveredMaskDetails}
          area={area}
          setArea={setArea}
        />
        <BlackObjectAnalysisDetailsContent
          showAllMasks={showAllMasks}
          filteredData={maskData}
          image={imageToShow}
          showBBox={showBBox}
          setHoveredMaskDetails={setHoveredMaskDetails}
        />
      </div>
      {/* <div className="info-container">
        <BlackObjectAnalysisAttributes data={semAnalysisMaskData} />
      </div> */}
    </Drawer>
  );
};

export default BlackObjectAnalysisDetails;
